import React, { useState, useEffect, useRef, Fragment } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { yellow } from "@mui/material/colors";
import axios from "axios";
import cls from "./UpperSgnBrd.module.css";
import diffCls from "../common/RealtimeDiff.module.css";
import DiffBadge from "../common/DiffBadge";
import {
  computeSignboardDiff,
  DIFF_DISPLAY_MS,
  SIGNBOARD_KEYS,
} from "../../lib/statusBoardDiff";
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import markerRdvIcon from "../../icon/marker-rdv.svg";
import carPumpIcon from "../../icon/fire_pump_truck_marker_64px.png";
import carOffroadIcon from "../../icon/fire_offroad_pump_truck_marker_64px.png";
import carTankIcon from "../../icon/fire_water_tanker_marker_64px.png";
import carChemicalIcon from "../../icon/fire_chemical_truck_marker_64px.png";
import carRescueIcon from "../../icon/fire_rescue_truck_marker_64px.png";
import carAmbulanceIcon from "../../icon/fire_ambulance_marker_64px.png";
import carHelicopterIcon from "../../icon/fire_helicopter_marker_64px.png";
import carCommandIcon from "../../icon/fire_command_van_marker_64px.png";
import carRecoveryIcon from "../../icon/fire_recovery_truck_marker_64px.png";
import carAdminIcon from "../../icon/fire_admin_car_marker_64px.png";
import carSatelliteIcon from "../../icon/fire_satellite_van_marker_64px.png";
import carPoliceIcon from "../../icon/police_car_marker_64px.png";
import carGovernmentIcon from "../../icon/government_car_marker_64px.png";
import carVolunteerIcon from "../../icon/volunteer_fire_truck_marker_64px.png";

function getEqpIcon(mfeqp) {
  const eqp = (mfeqp || "").trim();
  if (eqp === "소방펌프" || eqp === "펌프" || eqp.includes("펌프")) return carPumpIcon;
  if (eqp === "소방험지" || eqp.includes("험지")) return carOffroadIcon;
  if (eqp === "소방물탱크" || eqp === "물탱크" || eqp.includes("물탱크")) return carTankIcon;
  if (eqp === "소방화학" || eqp.includes("화학")) return carChemicalIcon;
  if (eqp === "소방구조" || eqp === "구조" || eqp.includes("구조")) return carRescueIcon;
  if (eqp === "소방구급" || eqp === "구급" || eqp.includes("구급")) return carAmbulanceIcon;
  if (eqp === "소방헬기" || eqp.includes("헬기")) return carHelicopterIcon;
  if (eqp === "소방지휘" || eqp === "지휘" || eqp.includes("지휘")) return carCommandIcon;
  if (eqp === "소방회복" || eqp.includes("회복")) return carRecoveryIcon;
  if (eqp === "소방행정" || eqp.includes("행정")) return carAdminIcon;
  if (eqp.includes("위성")) return carSatelliteIcon;
  if (eqp.includes("경찰")) return carPoliceIcon;
  if (eqp.includes("공무원")) return carGovernmentIcon;
  if (eqp.includes("의소대")) return carVolunteerIcon;
  return markerRdvIcon;
}

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

const FLY_DURATION_MS = 600;
const FLY_ICON_SIZE = 54; /* 이전 36 대비 50% 확대 */

function FlyingIcon({ from, to, statusRefMap, iconSrc, onStart, onEnd }) {
  const [pos, setPos] = useState(null);
  const [flying, setFlying] = useState(false);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    const fromEl = statusRefMap[from]?.current;
    const toEl = statusRefMap[to]?.current;
    if (!fromEl || !toEl) return;
    const run = () => {
      const r1 = fromEl.getBoundingClientRect();
      const r2 = toEl.getBoundingClientRect();
      const half = FLY_ICON_SIZE / 2;
      setPos({
        x: r1.left + r1.width / 2 - half,
        y: r1.top + r1.height / 2 - half,
        endX: r2.left + r2.width / 2 - half,
        endY: r2.top + r2.height / 2 - half,
      });
      requestAnimationFrame(() => requestAnimationFrame(() => {
        onStart?.(from);
        setFlying(true);
      }));
    };
    const t = setTimeout(run, 50);
    return () => clearTimeout(t);
  }, [from, to, statusRefMap]);

  const handleTransitionEnd = () => {
    if (!flying) return;
    onEndRef.current?.(to);
  };

  if (!pos) return null;
  return (
    <div
      role="presentation"
      aria-hidden
      className={cls.flyingIcon}
      style={{
        left: pos.x,
        top: pos.y,
        width: FLY_ICON_SIZE,
        height: FLY_ICON_SIZE,
        transform: flying ? `translate(${pos.endX - pos.x}px, ${pos.endY - pos.y}px)` : "translate(0,0)",
        transition: flying ? `transform ${FLY_DURATION_MS}ms ease-out` : "none",
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      <img
        src={iconSrc || markerRdvIcon}
        alt=""
        width={FLY_ICON_SIZE}
        height={FLY_ICON_SIZE}
        draggable={false}
        style={{ display: "block", width: "100%", height: "100%", pointerEvents: "none" }}
      />
    </div>
  );
}

export default function UpperSgnBrd(props) {
  const [list, setList] = useState([]);
  const [rdvCount, setRdvCount] = useState({ fireCount: 0, otherCount: 0 });
  const [signboardDiff, setSignboardDiff] = useState({});
  const signboardDiffTimerRef = useRef(null);
  const [flyingTransitions, setFlyingTransitions] = useState([]);
  const [blinkToKey, setBlinkToKey] = useState(null); // 종료 시점 반짝임용 (state로 넣어야 재렌더 후에도 유지)
  const initRef = useRef(0); // 초기값 저장 useRef

  // 엘레멘트접근 useRef
  const tRef = useRef();
  const sRef = useRef();
  const wRef = useRef();
  const mRef = useRef();
  const aRef = useRef();
  const rRef = useRef();
  const supportRef = useRef();

  // 현재값 저장 useRef
  const NowtotlRef = useRef(0);
  const NowstrtRef = useRef();
  const NowwaitRef = useRef(0);
  const NowmoveRef = useRef(0);
  const NowactvRef = useRef(0);
  const NowrtrnRef = useRef(0);

  // 이전값 저장 useRef
  const BeforetotlRef = useRef(0);
  const BeforestrtRef = useRef(0);
  const BeforewaitRef = useRef(0);
  const BeforemoveRef = useRef(0);
  const BeforeactvRef = useRef(0);
  const BeforertrnRef = useRef(0);
  const BeforeSupportRef = useRef(0);

  // 전환 애니메이션용: 이전 Before를 보존 (saveBeforeRef 덮어쓰기 전에 복사)
  const prevBeforeRef = useRef({ strt: 0, wait: 0, move: 0, actv: 0, rtrn: 0 });
  const flyingIdRef = useRef(0);
  const timelineOverrideRef = useRef(false);

  const STATUS_KEYS = ["strt", "wait", "move", "actv", "rtrn"];
  const statusRefMap = { strt: sRef, wait: wRef, move: mRef, actv: aRef, rtrn: rRef };
  const statusBlinkClassMap = { strt: cls.greyblink, wait: cls.greenblink, move: cls.blueblink, actv: cls.redblink, rtrn: cls.greyblink };
  const signboardBlinkMap = {
    totl: [tRef, cls.blackblink],
    strt: [sRef, cls.greyblink],
    wait: [wRef, cls.greenblink],
    move: [mRef, cls.blueblink],
    actv: [aRef, cls.redblink],
    rtrn: [rRef, cls.greyblink],
    support: [supportRef, cls.yellowblink],
  };

  const applySignboardDiff = (prevSnap, currSnap) => {
    if (initRef.current <= 0) return;
    const next = computeSignboardDiff(prevSnap, currSnap);
    const hasChange = Object.values(next).some((d) => d?.changed);
    if (!hasChange) return;
    setSignboardDiff(next);
    if (signboardDiffTimerRef.current) clearTimeout(signboardDiffTimerRef.current);
    signboardDiffTimerRef.current = setTimeout(() => {
      setSignboardDiff({});
      signboardDiffTimerRef.current = null;
    }, DIFF_DISPLAY_MS);
  };

  const triggerStatusBlink = (statusKey) => {
    const ref = statusRefMap[statusKey];
    const blinkClass = statusBlinkClassMap[statusKey];
    if (!ref?.current || !blinkClass) return;
    ref.current.classList.add(blinkClass);
    const t = setTimeout(() => { ref.current?.classList.remove(blinkClass); }, 3000);
    return () => clearTimeout(t);
  };

  // 종료 시점(to) 반짝임: 3초 후 제거
  useEffect(() => {
    if (!blinkToKey) return;
    const t = setTimeout(() => setBlinkToKey(null), 3000);
    return () => clearTimeout(t);
  }, [blinkToKey]);

  useEffect(() => {
    const onTimelineSignboard = (e) => {
      const { active, signboard, diff } = e.detail || {};
      timelineOverrideRef.current = !!active;
      if (!active) {
        setSignboardDiff({});
        return;
      }
      if (!signboard) return;
      const support = BeforeSupportRef.current ?? 0;
      const item = {
        totl: signboard.totl ?? 0,
        strt: signboard.strt ?? 0,
        wait: signboard.wait ?? 0,
        move: signboard.move ?? 0,
        actv: signboard.actv ?? 0,
        rtrn: signboard.rtrn ?? 0,
        support,
      };
      NowtotlRef.current = item.totl;
      NowstrtRef.current = item.strt;
      NowwaitRef.current = item.wait;
      NowmoveRef.current = item.move;
      NowactvRef.current = item.actv;
      NowrtrnRef.current = item.rtrn;
      setList([item]);
      setSignboardDiff(diff || {});
    };
    window.addEventListener("statusTimelineSignboard", onTimelineSignboard);
    return () =>
      window.removeEventListener("statusTimelineSignboard", onTimelineSignboard);
  }, []);

  useEffect(() => {
    const getStatus = async () => {
      if (timelineOverrideRef.current) return;
      const dsid = (sessionStorage.ondsid || "").trim();
      if (!dsid || !props.apiAddr) return;
      const wstring = `${props.apiAddr}/get/signboard?dsid=${encodeURIComponent(dsid)}`;
      try {
        const result = await axios.get(wstring);
        const responseData = result.data;
        const arr = Array.isArray(responseData) ? responseData : (responseData?.data && Array.isArray(responseData.data) ? responseData.data : []);
        if (arr.length) {
          const prevSnap = {
            totl: BeforetotlRef.current,
            strt: BeforestrtRef.current,
            wait: BeforewaitRef.current,
            move: BeforemoveRef.current,
            actv: BeforeactvRef.current,
            rtrn: BeforertrnRef.current,
            support: BeforeSupportRef.current,
          };
          const currSnap = {
            totl: arr[0].totl,
            strt: arr[0].strt,
            wait: arr[0].wait,
            move: arr[0].move,
            actv: arr[0].actv,
            rtrn: arr[0].rtrn,
            support: BeforeSupportRef.current,
          };
          applySignboardDiff(prevSnap, currSnap);
          saveNowRef(arr);
          setList(arr);
        }
      } catch (err) {
        console.warn("[UpperSgnBrd] signboard 요청 실패:", err?.message || err);
      }
    };
    getStatus();
  }, [props.apiAddr, props.viewSGN]);

  useEffect(() => {
    const dsid = (sessionStorage.ondsid || "").trim();
    if (!dsid || !props.apiAddr) return;
    const fetchRdvCount = async () => {
      try {
        const res = await axios.get(`${props.apiAddr}/get/rdvattendanceCount?dsid=${encodeURIComponent(dsid)}`);
        const newSupport =
          (res.data.fireCount || 0) + (res.data.otherCount || 0);
        if (initRef.current > 0) {
          applySignboardDiff(
            {
              totl: NowtotlRef.current,
              strt: NowstrtRef.current,
              wait: NowwaitRef.current,
              move: NowmoveRef.current,
              actv: NowactvRef.current,
              rtrn: NowrtrnRef.current,
              support: BeforeSupportRef.current,
            },
            {
              totl: NowtotlRef.current,
              strt: NowstrtRef.current,
              wait: NowwaitRef.current,
              move: NowmoveRef.current,
              actv: NowactvRef.current,
              rtrn: NowrtrnRef.current,
              support: newSupport,
            },
          );
        }
        setRdvCount({ fireCount: res.data.fireCount || 0, otherCount: res.data.otherCount || 0 });
      } catch (e) {
        // 요청 실패 시 이전 값 유지(지원인력이 0으로 깜빡이는 현상 방지)
        console.warn("[UpperSgnBrd] rdvattendanceCount 요청 실패, 이전 값 유지:", e?.message || e);
      }
    };
    fetchRdvCount();
  }, [props.apiAddr, props.viewSGN]);

  // 상태 전환(from→to) 계산 후 flying 애니메이션 큐에 추가
  useEffect(() => {
    if (!list.length || initRef.current <= 1) return;
    if (timelineOverrideRef.current) return;
    const prev = prevBeforeRef.current;
    const now = {
      strt: NowstrtRef.current,
      wait: NowwaitRef.current,
      move: NowmoveRef.current,
      actv: NowactvRef.current,
      rtrn: NowrtrnRef.current,
    };
    const decreases = [];
    const increases = [];
    STATUS_KEYS.forEach((key) => {
      const delta = (now[key] ?? 0) - (prev[key] ?? 0);
      if (delta === -1) decreases.push(key);
      if (delta === 1) increases.push(key);
    });
    const isStateChange = typeof sessionStorage !== "undefined" && sessionStorage.refreshSort === "stateChaged";
    const changedEqp = isStateChange && sessionStorage.refreshEqp ? String(sessionStorage.refreshEqp).trim() : "";
    const newFlyings = [];
    for (let i = 0; i < Math.min(decreases.length, increases.length); i++) {
      flyingIdRef.current += 1;
      newFlyings.push({
        id: flyingIdRef.current,
        from: decreases[i],
        to: increases[i],
        eqp: i === 0 ? changedEqp : "",
      });
    }
    if (newFlyings.length) setFlyingTransitions((prevList) => [...prevList, ...newFlyings]);
  }, [list]);

  const saveNowRef = (data) => { // 현재값 저장
    NowtotlRef.current = data[0].totl;
    NowstrtRef.current = data[0].strt;
    NowwaitRef.current = data[0].wait;
    NowmoveRef.current = data[0].move;
    NowactvRef.current = data[0].actv;
    NowrtrnRef.current = data[0].rtrn;
    
  }

  const saveBeforeRef = (item) => { // 이전값 저장
    BeforetotlRef.current = item.totl;
    BeforestrtRef.current = item.strt;
    BeforewaitRef.current = item.wait;
    BeforemoveRef.current = item.move;
    BeforeactvRef.current = item.actv;
    BeforertrnRef.current = item.rtrn;
  }

  // 변경된 칸 배경 깜빡임(blink) — 리뷰: diff 있는 칸만, 실시간: sessionStorage 플래그
  useEffect(() => {
    if (!list.length) return;
    initRef.current = initRef.current + 1;
    if (initRef.current <= 1) return;
    const safeAdd = (ref, className) => { if (ref?.current) ref.current.classList.add(className); };
    const safeRemove = (ref, className) => { if (ref?.current) ref.current.classList.remove(className); };
    const removeAll = () => {
      safeRemove(tRef, cls.blackblink);
      safeRemove(sRef, cls.greyblink);
      safeRemove(wRef, cls.greenblink);
      safeRemove(mRef, cls.blueblink);
      safeRemove(aRef, cls.redblink);
      safeRemove(rRef, cls.greyblink);
      safeRemove(supportRef, cls.yellowblink);
    };
    removeAll();

    if (timelineOverrideRef.current) {
      const changedKeys = SIGNBOARD_KEYS.filter(
        (key) => signboardDiff[key]?.changed,
      );
      if (!changedKeys.length) return undefined;
      changedKeys.forEach((key) => {
        const entry = signboardBlinkMap[key];
        if (entry) safeAdd(entry[0], entry[1]);
      });
      const t = setTimeout(removeAll, 3000);
      return () => clearTimeout(t);
    }

    if (typeof sessionStorage !== "undefined") {
      if (sessionStorage.sgnTotl === "true") safeAdd(tRef, cls.blackblink);
      if (sessionStorage.sgnStrt === "true") safeAdd(sRef, cls.greyblink);
      if (sessionStorage.sgnWait === "true") safeAdd(wRef, cls.greenblink);
      if (sessionStorage.sgnMove === "true") safeAdd(mRef, cls.blueblink);
      if (sessionStorage.sgnActv === "true") safeAdd(aRef, cls.redblink);
      if (sessionStorage.sgnRtrn === "true") safeAdd(rRef, cls.greyblink);
      if (sessionStorage.sgnSupport === "true") safeAdd(supportRef, cls.yellowblink);
    }
    const t = setTimeout(removeAll, 3000);
    return () => clearTimeout(t);
  }, [list, flyingTransitions, signboardDiff]);

  const signBrd = (Array.isArray(list) ? list : []).map((item, index) => {
    // 전환 애니메이션용: saveBeforeRef 덮어쓰기 전에 이전 Before 보존
    prevBeforeRef.current = {
      strt: BeforestrtRef.current,
      wait: BeforewaitRef.current,
      move: BeforemoveRef.current,
      actv: BeforeactvRef.current,
      rtrn: BeforertrnRef.current,
    };
    return (
    <Fragment key={`sgn-row-${index}`}>
      {/* {console.log(NowwaitRef.current + '/' + BeforewaitRef.current)} */}
      {(() => {
        if (timelineOverrideRef.current) return null;
        NowtotlRef.current != BeforetotlRef.current ? sessionStorage.sgnTotl = true : sessionStorage.sgnTotl = false;
        NowstrtRef.current != BeforestrtRef.current ? sessionStorage.sgnStrt = true : sessionStorage.sgnStrt = false;
        NowwaitRef.current != BeforewaitRef.current ? sessionStorage.sgnWait = true : sessionStorage.sgnWait = false;
        NowmoveRef.current != BeforemoveRef.current ? sessionStorage.sgnMove = true : sessionStorage.sgnMove = false;
        NowactvRef.current != BeforeactvRef.current ? sessionStorage.sgnActv = true : sessionStorage.sgnActv = false;
        NowrtrnRef.current != BeforertrnRef.current ? sessionStorage.sgnRtrn = true : sessionStorage.sgnRtrn = false;
        const supportTotal = rdvCount.fireCount + rdvCount.otherCount;
        supportTotal !== BeforeSupportRef.current
          ? (sessionStorage.sgnSupport = true)
          : (sessionStorage.sgnSupport = false);
        return null;
      })()}

      <LightTooltip title="전체 국가동원 자원 현황 (클릭 시 상세보기)" placement="top" arrow>
        <div key="sgn-totl" className={`${cls.sgnbox} ${cls.black}`} ref={tRef} onClick={() => props.onSignboardClick?.("all")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && props.onSignboardClick?.("all")} style={{ cursor: props.onSignboardClick ? "pointer" : undefined }}>
          <Stack direction="column" alignItems="center">
            <Typography variant="h6" mb={-1.5} >전체</Typography>
            <Typography variant="h4" align="center">{item.totl}</Typography>
            <span className={diffCls.signboardDiff}><DiffBadge diff={signboardDiff.totl} /></span>
          </Stack>
        </div>
      </LightTooltip>

      <LightTooltip title="각 시도에서 동원령이후 재난지역으로 이동중 (클릭 시 해당 자원만 상세보기)" placement="top" >
        <div key="sgn-strt" className={`${cls.sgnbox} ${cls.grey} ${blinkToKey === "strt" ? cls.greyblink : ""}`} ref={sRef} onClick={() => props.onSignboardClick?.(9)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && props.onSignboardClick?.(9)} style={{ cursor: props.onSignboardClick ? "pointer" : undefined }}>
          <Stack direction="column" alignItems="center">
            <Typography variant="h6" mb={-1.5} >집결중</Typography>
            <Typography variant="h4" align="center">{item.strt}</Typography>
            <span className={diffCls.signboardDiff}><DiffBadge diff={signboardDiff.strt} /></span>
          </Stack>
        </div>
      </LightTooltip>

      <LightTooltip title="자원집결지에서 대기중 (클릭 시 해당 자원만 상세보기)" placement="top">
        <div key="sgn-wait" className={`${cls.sgnbox} ${cls.green} ${blinkToKey === "wait" ? cls.greenblink : ""}`} ref={wRef} onClick={() => props.onSignboardClick?.(0)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && props.onSignboardClick?.(0)} style={{ cursor: props.onSignboardClick ? "pointer" : undefined }}>
          <Stack direction="column" alignItems="center">
            <Typography variant="h6" mb={-1.5} >대기</Typography>
            <Typography variant="h4" align="center">{item.wait}</Typography>
            <span className={diffCls.signboardDiff}><DiffBadge diff={signboardDiff.wait} /></span>
          </Stack>
        </div>
      </LightTooltip>

      <LightTooltip title="이동중 (클릭 시 해당 자원만 상세보기)" placement="top" >
        <div key="sgn-move" className={`${cls.sgnbox} ${cls.blue} ${blinkToKey === "move" ? cls.blueblink : ""}`} ref={mRef} onClick={() => props.onSignboardClick?.(1)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && props.onSignboardClick?.(1)} style={{ cursor: props.onSignboardClick ? "pointer" : undefined }}>
          <Stack direction="column" alignItems="center">
            <Typography variant="h6" mb={-1.5} >이동</Typography>
            <Typography variant="h4" align="center">{item.move}</Typography>
            <span className={diffCls.signboardDiff}><DiffBadge diff={signboardDiff.move} /></span>
          </Stack>
        </div>
      </LightTooltip>

      <LightTooltip title="재난현장 활동중 (클릭 시 해당 자원만 상세보기)" placement="top" >
        <div key="sgn-actv" className={`${cls.sgnbox} ${cls.red} ${blinkToKey === "actv" ? cls.redblink : ""}`} ref={aRef} onClick={() => props.onSignboardClick?.(2)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && props.onSignboardClick?.(2)} style={{ cursor: props.onSignboardClick ? "pointer" : undefined }}>
          <Stack direction="column" alignItems="center">
            <Typography variant="h6" mb={-1.5} >활동</Typography>
            <Typography variant="h4" align="center">{item.actv}</Typography>
            <span className={diffCls.signboardDiff}><DiffBadge diff={signboardDiff.actv} /></span>
          </Stack>
        </div>
      </LightTooltip>

      <LightTooltip title="원소속 시도로 복귀 (클릭 시 해당 자원만 상세보기)" placement="top" arrow style={{marginRight:"3px"}}>
        <div id="uppersgn" key="sgn-rtrn" className={`${cls.sgnbox} ${cls.grey} ${blinkToKey === "rtrn" ? cls.greyblink : ""}`} ref={rRef} onClick={() => props.onSignboardClick?.(3)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && props.onSignboardClick?.(3)} style={{ cursor: props.onSignboardClick ? "pointer" : undefined }}>
          <Stack direction="column" alignItems="center">
            <Typography variant="h6" mb={-1.5} sx={{ letterSpacing:"0px" }}>원대복귀</Typography>
            <Typography variant="h4" align="center">{item.rtrn}</Typography>
            <span className={diffCls.signboardDiff}><DiffBadge diff={signboardDiff.rtrn} /></span>
          </Stack>
        </div>
      </LightTooltip>
      <LightTooltip title="지원인력 출근 현황 (미퇴근 기준) (클릭 시 전체 지원인력현황)" placement="top" arrow>
        <div ref={supportRef} className={cls.sgnbox} style={{ minWidth: 100, marginLeft: 16, backgroundColor: yellow[800], color: "rgba(0,0,0,0.87)", cursor: props.onSignboardClick ? "pointer" : undefined }} onClick={() => props.onSignboardClick?.("support")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && props.onSignboardClick?.("support")}>
          <Stack direction="column" alignItems="center">
            <Typography variant="h6" mb={-1.5}>지원인력</Typography>
            <Typography variant="h4" align="center">{rdvCount.fireCount + rdvCount.otherCount}</Typography>
            <span className={diffCls.signboardDiff}><DiffBadge diff={signboardDiff.support} /></span>
          </Stack>
        </div>
      </LightTooltip>
      {saveBeforeRef(item)}
      {(() => { BeforeSupportRef.current = rdvCount.fireCount + rdvCount.otherCount; return null; })()}
    </Fragment>
    );
  });

  let upsgbpos ;
  const getposuppersgnbrd = () => { 
    const windowWidth = window.innerWidth;
    upsgbpos = windowWidth/2 - 425;
  }
  getposuppersgnbrd();

  const removeFlying = (id) => {
    setFlyingTransitions((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <Box
      id="uppersgn"
      sx={{
        position: "fixed",
        top: 0,
        left: upsgbpos,
        width: 850,
        minHeight: 60,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
        zIndex: 10000,
      }}
    >
      <Stack
        direction="row"
        gap={1}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          width: "fit-content",
          height: "60px",
          minHeight: "60px",
          flexShrink: 0,
          padding: "2px 6px", /* 상하 2px, 좌우 6px(기존 2px + 4px) */
          borderRadius: "5px",
          backgroundColor: "lightgreen",
          zIndex: 10,
        }}
      >
        {signBrd}
      </Stack>
      {flyingTransitions.map((f) => (
        <FlyingIcon
          key={f.id}
          from={f.from}
          to={f.to}
          statusRefMap={statusRefMap}
          iconSrc={f.eqp ? getEqpIcon(f.eqp) : undefined}
          onStart={triggerStatusBlink}
          onEnd={(toKey) => {
            setBlinkToKey(toKey);
            removeFlying(f.id);
          }}
        />
      ))}
    </Box>
  );
}
